import { create } from "zustand"
import { useAuthStore } from "./auth-store"

export type ErrorSource = "react" | "runtime" | "promise"

export interface ErrorEntry {
  id: string
  timestamp: number
  message: string
  source: ErrorSource
  stack?: string
  componentStack?: string
  url: string
  fileLocation?: string // e.g. "app.js:42:16" for runtime errors
  userAgent: string
  userId?: string
  count: number
}

/** Common context auto-populated by addError */
export function getErrorContext() {
  return {
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    userId: useAuthStore.getState().user?._id,
  }
}

interface ErrorOverlayState {
  errors: ErrorEntry[]
  isOpen: boolean
  maxErrors: number

  addError: (entry: Omit<ErrorEntry, "id" | "count" | "url" | "userAgent" | "userId" | "timestamp"> & Partial<Pick<ErrorEntry, "url" | "userAgent" | "userId" | "timestamp">>) => void
  clearAll: () => void
  removeError: (id: string) => void
  setOpen: (open: boolean) => void
  toggleOpen: () => void
}

export const useErrorOverlayStore = create<ErrorOverlayState>((set) => ({
  errors: [],
  isOpen: false,
  maxErrors: 50,

  addError: (entry) =>
    set((state) => {
      const ctx = getErrorContext()
      const full = { ...ctx, ...entry, timestamp: entry.timestamp ?? ctx.timestamp }

      // Dedup: same message+stack within 2s → increment count
      const existingIdx = state.errors.findIndex(
        (e) =>
          e.message === full.message &&
          e.stack === full.stack &&
          full.timestamp - e.timestamp < 2000,
      )
      if (existingIdx !== -1) {
        const updated = [...state.errors]
        updated[existingIdx] = {
          ...updated[existingIdx],
          count: updated[existingIdx].count + 1,
          timestamp: full.timestamp,
        }
        return { errors: updated }
      }

      const newEntry: ErrorEntry = {
        ...full,
        id: crypto.randomUUID?.() ?? String(Date.now()) + Math.random(),
        count: 1,
      }
      const errors = [newEntry, ...state.errors].slice(0, state.maxErrors)
      return { errors, isOpen: true }
    }),

  clearAll: () => set({ errors: [], isOpen: false }),

  removeError: (id) =>
    set((state) => {
      const errors = state.errors.filter((e) => e.id !== id)
      return { errors, isOpen: errors.length > 0 ? state.isOpen : false }
    }),

  setOpen: (open) => set({ isOpen: open }),

  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
}))
